import React, { useState, useEffect } from "react";
import { urlFor } from "../functions";
import placeholder from "../media/placeholder-image.png";

/**
 * Card component for displaying image cards with titles and optional publish dates
 * @param {string} style - Card style ('featured' or default)
 * @param {Object} image - Sanity image object
 * @param {string} title - Card title
 * @param {string} link - Card link (unused but kept for compatibility)
 * @param {string} publishDate - Optional publish date
 */
function Card({ style, image, title, link, publishDate }) {
  const [source, setSource] = useState(placeholder);
  const [isLoading, setIsLoading] = useState(true);
  const cardStyle = style === "featured" ? "featured-card" : "card";

  useEffect(() => {
    // Reset to placeholder immediately when image prop changes
    setSource(placeholder);
    setIsLoading(true);

    // Handle the case where image is undefined or null
    if (!image) {
      setSource(placeholder);
      setIsLoading(false);
      return;
    }

    try {
      const imageUrl = urlFor(image).auto("format").fit("max").toString();
      
      // Preload the image to ensure it's ready before setting
      const img = new Image();
      img.onload = () => {
        setSource(imageUrl);
        setIsLoading(false);
      };
      img.onerror = () => {
        setSource(placeholder);
        setIsLoading(false);
      };
      img.src = imageUrl;
    } catch (error) {
      console.error("Error loading image:", error);
      setSource(placeholder);
      setIsLoading(false);
    }
  }, [image]);

  const titlePosition = publishDate ? "items-start" : "items-center";

  return (
    <div className={`${cardStyle} ${titlePosition}`}>
      <div
        className="card-image"
        style={{
          backgroundImage: `url(${source})`,
          opacity: isLoading ? 0.7 : 1,
          transition: "opacity 0.3s ease-in-out"
        }}
      ></div>
      <div className="card-container">
        {publishDate && (
          <span
            className="publish-date"
            style={{ color: "var(--primary-color)" }}
          >
            {publishDate}
          </span>
        )}
        <h3 className="card-title">{title}</h3>
      </div>
    </div>
  );
}

export default Card;
